export const REFUND_COMPENSATION_SCENARIOS = {
  APPLIED_TO_FUTURE_INSTALLMENTS: 'APPLIED_TO_FUTURE_INSTALLMENTS',
  PARTIALLY_APPLIED_WITH_RESIDUAL_REFUND:
    'PARTIALLY_APPLIED_WITH_RESIDUAL_REFUND',
  FULL_REFUND_TO_INSURED: 'FULL_REFUND_TO_INSURED',
};

export const REFUND_PAYOUT_METHODS = {
  PIX: 'PIX',
  BANK_ACCOUNT: 'BANK_ACCOUNT',
};

/**
 * @typedef {Object} NegativeEndorsementBillet
 * @property {number|null} id
 * @property {number|null} policyId
 * @property {string|null} gatewayId
 * @property {number|null} originalValue
 * @property {number|null} ourNumber
 * @property {string|null} dueDate
 * @property {string|null} installment
 */

/**
 * @typedef {Object} NegativeEndorsementInstallment
 * @property {number|null} id
 * @property {number|null} installmentNumber
 * @property {number|null} oldValue
 * @property {NegativeEndorsementBillet|null} billet
 */

/**
 * @typedef {Object} NegativeEndorsementChangedInstallment
 * @property {number|null} id
 * @property {number|null} installmentNumber
 * @property {number|null} oldValue
 * @property {number|null} newValue
 * @property {NegativeEndorsementBillet|null} billet
 */

/**
 * @typedef {Object} NegativeEndorsementInstallmentsResponse
 * @property {string|null} policyNumber
 * @property {number} refundValue
 * @property {NegativeEndorsementInstallment[]} installmentsToCancel
 * @property {NegativeEndorsementChangedInstallment|null} installmentToChangeValue
 */

export function getEmptyRefundCompensationData() {
  return {
    policyNumber: null,
    refundValue: 0,
    installmentsToCancel: [],
    installmentToChangeValue: null,
  };
}

export function normalizeRefundCompensationData(data) {
  if (!data) {
    return getEmptyRefundCompensationData();
  }

  return {
    policyNumber: data.policyNumber || null,
    refundValue: Number(data.refundValue || 0),
    installmentsToCancel: Array.isArray(data.installmentsToCancel)
      ? data.installmentsToCancel
      : [],
    installmentToChangeValue: data.installmentToChangeValue || null,
  };
}

export function hasInstallmentsToCancel(data) {
  return (
    data &&
    Array.isArray(data.installmentsToCancel) &&
    data.installmentsToCancel.length > 0
  );
}

export function hasInstallmentToChangeValue(data) {
  return Boolean(data && data.installmentToChangeValue);
}

export function hasImpactedInstallments(data) {
  return hasInstallmentsToCancel(data) || hasInstallmentToChangeValue(data);
}

export function shouldShowRefundPayoutSection(data) {
  return Boolean(data && Number(data.refundValue) > 0);
}

export function getRefundCompensationScenario(data) {
  const normalizedData = normalizeRefundCompensationData(data);
  const hasImpact = hasImpactedInstallments(normalizedData);
  const hasRefundValue = shouldShowRefundPayoutSection(normalizedData);

  if (hasImpact && !hasRefundValue) {
    return REFUND_COMPENSATION_SCENARIOS.APPLIED_TO_FUTURE_INSTALLMENTS;
  }

  if (hasImpact && hasRefundValue) {
    return REFUND_COMPENSATION_SCENARIOS.PARTIALLY_APPLIED_WITH_RESIDUAL_REFUND;
  }

  if (!hasImpact && hasRefundValue) {
    return REFUND_COMPENSATION_SCENARIOS.FULL_REFUND_TO_INSURED;
  }

  return null;
}

export function getRefundCompensationScenarioContent(scenario) {
  switch (scenario) {
    case REFUND_COMPENSATION_SCENARIOS.APPLIED_TO_FUTURE_INSTALLMENTS:
      return {
        title: 'Desconto nas próximas parcelas',
        description:
          'As parcelas pendentes de pagamento serão ajustadas ou canceladas.',
        color: 'success',
      };
    case REFUND_COMPENSATION_SCENARIOS.PARTIALLY_APPLIED_WITH_RESIDUAL_REFUND:
      return {
        title: 'Desconto nas próximas parcelas e transferência bancária',
        description:
          'As parcelas pendentes de pagamento serão ajustadas ou canceladas, e o valor remanescente será restituído via transferência bancária.',
        color: 'warning',
      };
    case REFUND_COMPENSATION_SCENARIOS.FULL_REFUND_TO_INSURED:
      return {
        title: 'Transferência bancária',
        description:
          'O valor será restituído ao segurado via transferência bancária.',
        color: 'primary',
      };
    default:
      return {
        title: 'Forma de compensação da restituição',
        description: '',
        color: 'primary',
      };
  }
}

export function formatCurrencyBRL(value) {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatDateBR(value) {
  if (!value) {
    return '-';
  }

  const [datePart] = `${value}`.split('T');

  if (!datePart || !datePart.includes('-')) {
    return '-';
  }

  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}`;
}

export function isCancellationOperationType(operationType) {
  return Boolean(
    operationType &&
      typeof operationType === 'object' &&
      operationType.isCancellation,
  );
}

function getCancelledInstallmentPreviousValue(installment) {
  if (!installment) {
    return 0;
  }

  if (installment.oldValue !== null && installment.oldValue !== undefined) {
    return Number(installment.oldValue);
  }

  if (
    installment.billet &&
    installment.billet.originalValue !== null &&
    installment.billet.originalValue !== undefined
  ) {
    return Number(installment.billet.originalValue);
  }

  return 0;
}

export function formatImpactedInstallments(data) {
  const normalizedData = normalizeRefundCompensationData(data);
  const installments = [];

  if (normalizedData.installmentToChangeValue) {
    const changedInstallment = normalizedData.installmentToChangeValue;

    installments.push({
      id: changedInstallment.id,
      installmentNumber: changedInstallment.installmentNumber,
      installmentLabel:
        changedInstallment.billet && changedInstallment.billet.installment
          ? changedInstallment.billet.installment
          : `Parcela ${changedInstallment.installmentNumber}`,
      dueDate: formatDateBR(
        changedInstallment.billet && changedInstallment.billet.dueDate,
      ),
      previousValue: changedInstallment.oldValue,
      newValue: changedInstallment.newValue,
      status: 'Alterada',
      statusColor: 'warning',
      type: 'changed',
    });
  }

  normalizedData.installmentsToCancel.forEach((installment) => {
    installments.push({
      id: installment.id,
      installmentNumber: installment.installmentNumber,
      installmentLabel:
        installment.billet && installment.billet.installment
          ? installment.billet.installment
          : `Parcela ${installment.installmentNumber}`,
      dueDate: formatDateBR(installment.billet && installment.billet.dueDate),
      previousValue: getCancelledInstallmentPreviousValue(installment),
      newValue: null,
      status: 'Cancelada',
      statusColor: 'danger',
      type: 'cancelled',
    });
  });

  return installments.sort(
    (currentInstallment, nextInstallment) =>
      currentInstallment.installmentNumber - nextInstallment.installmentNumber,
  );
}

export function getDefaultRefundPayoutMethod(refundData) {
  if (refundData && refundData.bankId) {
    return REFUND_PAYOUT_METHODS.BANK_ACCOUNT;
  }

  return REFUND_PAYOUT_METHODS.PIX;
}

export function getAvailableRefundPayoutMethods(refundData) {
  const methods = [];

  if (
    refundData &&
    (refundData.bankId ||
      refundData.agency ||
      refundData.account ||
      refundData.accountDigit)
  ) {
    methods.push(REFUND_PAYOUT_METHODS.BANK_ACCOUNT);
  }

  if (refundData && refundData.pixKey) {
    methods.push(REFUND_PAYOUT_METHODS.PIX);
  }

  return methods;
}

export function normalizeRefundDataForPayoutMethod(refundData, payoutMethod) {
  const normalizedRefundData = {
    bankId: refundData && refundData.bankId ? refundData.bankId : null,
    agency: refundData && refundData.agency ? refundData.agency : null,
    account: refundData && refundData.account ? refundData.account : null,
    accountDigit:
      refundData && refundData.accountDigit ? refundData.accountDigit : null,
    pixKey: refundData && refundData.pixKey ? refundData.pixKey : null,
    holderName:
      refundData && refundData.holderName ? refundData.holderName : null,
    holderDocument:
      refundData && refundData.holderDocument
        ? refundData.holderDocument
        : null,
  };

  if (payoutMethod === REFUND_PAYOUT_METHODS.BANK_ACCOUNT) {
    normalizedRefundData.pixKey = null;
    return normalizedRefundData;
  }

  normalizedRefundData.bankId = null;
  normalizedRefundData.agency = null;
  normalizedRefundData.account = null;
  normalizedRefundData.accountDigit = null;

  return normalizedRefundData;
}

export function hasRefundPayoutData(refundData, payoutMethod) {
  if (!refundData) {
    return false;
  }

  if (!payoutMethod) {
    return Boolean(
      refundData.bankId ||
        refundData.agency ||
        refundData.account ||
        refundData.accountDigit ||
        refundData.pixKey ||
        refundData.holderName ||
        refundData.holderDocument,
    );
  }

  if (payoutMethod === REFUND_PAYOUT_METHODS.BANK_ACCOUNT) {
    return Boolean(
      refundData.bankId ||
        refundData.agency ||
        refundData.account ||
        refundData.accountDigit ||
        refundData.holderName ||
        refundData.holderDocument,
    );
  }

  return Boolean(
    refundData.pixKey || refundData.holderName || refundData.holderDocument,
  );
}
